/**
 * Tasks, narrowed to one workflow.
 *
 * Two callers need this — `deletionSummary` (what deleting a whole workflow
 * takes with it) and `phaseDeletion` (what stands on a phase's steps) — and each
 * had its own filter and its own paragraph explaining why the filter is there.
 * A change to what "belongs to this workflow" means, such as excluding archived
 * tasks, would have had to be made twice.
 */

/**
 * The minimum a task must provide to be counted against a workflow.
 *
 * Structural rather than `TaskView`, following `SelectionScope` in
 * `builder/selection.ts`: these modules need three fields between them, and
 * taking the whole view would make every test construct a task no assertion
 * looks at. `position` is the step id the task currently sits on.
 */
export type WorkflowScopedTask = {
  workflow_id: string;
  title: string;
  position?: string;
};

/**
 * The workspace aggregate carries every task in the workspace, and step ids are
 * only unique within a workflow — so counting a neighbour's work would block a
 * deletion, or inflate a warning, for a reason nobody could see on screen.
 */
export function tasksOfWorkflow<T extends WorkflowScopedTask>(
  tasks: ReadonlyArray<T>,
  workflowId: string,
): T[] {
  return tasks.filter((task) => task.workflow_id === workflowId);
}
